import { useLingui } from "@lingui/react/macro";
import { CompanySelect, OrganizationSelect } from "@/components/common/entity-select";
import type { CatalogScope } from "@/hooks/useCatalogScope";

/**
 * The Organization and Concept pickers above a catalogue list (recipes, menus).
 * Which of the two appear, and which are locked, comes from `useCatalogScope`.
 */
export function CatalogScopeFilters({ scope }: { scope: CatalogScope }) {
  const { t } = useLingui();
  const {
    shouldShowOrgFilter,
    shouldShowCompanyFilter,
    effectiveOrganizationId,
    selectedOrganizationDisplayName,
    fixedOrganizationId,
    fixedCompanyId,
    selectedCompanyId,
    isInternalRecipeViewer,
    conceptAlwaysSelected,
    setSelectedOrganizationId,
    setSelectedCompanyId,
  } = scope;

  if (!shouldShowOrgFilter && !shouldShowCompanyFilter) return null;

  return (
    <div className="mb-3 flex flex-wrap items-end gap-3">
      {shouldShowOrgFilter && (
        <div className="w-72">
          <label className="mb-1 block text-xs text-(--color-text-secondary)">{t`Organization`}</label>
          <OrganizationSelect
            value={effectiveOrganizationId}
            displayValue={selectedOrganizationDisplayName}
            adminScope
            disabled={Boolean(fixedOrganizationId)}
            onChange={(orgId) => {
              setSelectedOrganizationId(orgId);
              setSelectedCompanyId(null);
            }}
            onClear={
              fixedOrganizationId
                ? undefined
                : () => {
                    setSelectedOrganizationId(null);
                    setSelectedCompanyId(null);
                  }
            }
            placeholder={t`Select organization`}
          />
        </div>
      )}

      {shouldShowCompanyFilter && (
        <div className="w-72">
          <label className="mb-1 block text-xs text-(--color-text-secondary)">{t`Concept`}</label>
          <CompanySelect
            value={selectedCompanyId ?? fixedCompanyId ?? null}
            organizationId={
              isInternalRecipeViewer
                ? (effectiveOrganizationId ?? undefined)
                : (fixedOrganizationId ?? undefined)
            }
            disabled={
              Boolean(fixedCompanyId) ||
              (isInternalRecipeViewer && !effectiveOrganizationId)
            }
            onChange={(companyId) => setSelectedCompanyId(companyId)}
            onClear={
              fixedCompanyId || conceptAlwaysSelected ? undefined : () => setSelectedCompanyId(null)
            }
            placeholder={t`Select concept`}
          />
        </div>
      )}
    </div>
  );
}
